import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import ViewsNav from '../components/ViewsNav';
import { setTaskDone } from '../lib/tasks';
import TaskCard from '../components/TaskCard';
import TaskModal from '../components/TaskModal';

const ALL_PROPERTIES = 'All Properties';
const PRIORITIES = ['All', 'High', 'Medium', 'Low'];
const STATUS_FILTERS = ['Open', 'Done', 'All'];

export default function Dashboard({ session, currentView, onNavigate }) {
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('All Properties');
  const [selectedPriority, setSelectedPriority] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('Open');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskPhotos, setTaskPhotos] = useState([]);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProperties();
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProperties = async () => {
    const { data } = await supabase.from('properties').select('*').order('name');
    if (data) setProperties(data);
  };

  const fetchTaskPhotos = async () => {
    const { data } = await supabase
      .from('task_photos')
      .select('*')
      .order('created_at');
    if (data && data.length > 0) {
      const withUrls = await Promise.all(
        data.map(async (photo) => {
          const { data: urlData } = await supabase.storage
            .from('task-photos')
            .createSignedUrl(photo.storage_path, 3600);
          return { ...photo, url: urlData?.signedUrl };
        })
      );
      setTaskPhotos(withUrls);
    } else {
      setTaskPhotos([]);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('tasks')
      .select('*, properties(name)')
      .order('created_at', { ascending: false });
    if (data) setTasks(data);
    if (error) console.error(error);
    setLoading(false);
    await fetchTaskPhotos();
  };

  const toggleDone = async (task) => {
    const newDone = !task.done;
    const { error, nextTaskId, nextDueDate } = await setTaskDone(task, newDone);
    if (error) return;

    let updatedTasks = tasks.map(t => t.id === task.id ? { ...t, done: newDone } : t);

    // A recurring task's next occurrence is created by the database (see lib/tasks.js)
    if (nextTaskId) {
      const { data: nextTask } = await supabase
        .from('tasks')
        .select('*, properties(name)')
        .eq('id', nextTaskId)
        .single();
      if (nextTask) {
        updatedTasks = [nextTask, ...updatedTasks];
        const formatted = new Date(nextDueDate + 'T12:00:00')
          .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        setToast(`Next occurrence created for ${formatted}`);
        setTimeout(() => setToast(null), 4000);
      }
    }

    setTasks(updatedTasks);
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    const { error } = await supabase.from('tasks').delete().eq('id', id);
    if (!error) setTasks(tasks.filter(t => t.id !== id));
  };

  const openEdit = (task) => { setEditingTask(task); setShowModal(true); };
  const openAdd = () => { setEditingTask(null); setShowModal(true); };

  const onSaved = async (savedTask, isNew) => {
    if (isNew) {
      setTasks([savedTask, ...tasks]);
    } else {
      setTasks(tasks.map(t => t.id === savedTask.id ? savedTask : t));
    }
    setShowModal(false);
    await fetchTaskPhotos();
  };

  // Filter tasks
  const filtered = tasks.filter(t => {
    const propMatch = selectedProperty === 'All Properties' || t.properties?.name === selectedProperty;
    const priMatch = selectedPriority === 'All' || t.priority === selectedPriority;
    const statusMatch = selectedStatus === 'All' || (selectedStatus === 'Open' ? !t.done : t.done);
    if (!propMatch || !priMatch || !statusMatch) return false;
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (t.title || '').toLowerCase().includes(term) ||
      (t.notes || '').toLowerCase().includes(term) ||
      (t.vendor || '').toLowerCase().includes(term) ||
      (t.properties?.name || '').toLowerCase().includes(term)
    );
  });

  // Stats
  const open = tasks.filter(t => !t.done).length;
  const today = new Date().toISOString().split('T')[0];
  const overdue = tasks.filter(t => !t.done && t.due_date && t.due_date < today).length;
  const totalCost = tasks.filter(t => !t.done).reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0);

  // Counts for sidebar
  const countFor = (prop) => tasks.filter(t => !t.done && (prop === 'All Properties' || t.properties?.name === prop)).length;

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>

        <ViewsNav currentView={currentView} onNavigate={onNavigate} />

        <div className="sidebar-section">
          <div className="sidebar-section-label">Properties</div>
          <div
            className={`sidebar-item ${selectedProperty === ALL_PROPERTIES ? 'active' : ''}`}
            onClick={() => setSelectedProperty(ALL_PROPERTIES)}
          >
            All Properties <span className="count">{countFor(ALL_PROPERTIES)}</span>
          </div>
          {properties.map(p => (
            <div
              key={p.id}
              className="sidebar-item sidebar-property-link"
              onClick={() => onNavigate('property-hub', p.id)}
            >
              {p.name} <span className="sidebar-arrow">&rarr;</span>
            </div>
          ))}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-label">Priority</div>
          {PRIORITIES.map(p => (
            <div
              key={p}
              className={`sidebar-item ${selectedPriority === p ? 'active' : ''}`}
              onClick={() => setSelectedPriority(p)}
            >
              {p}
            </div>
          ))}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-label">Status</div>
          {STATUS_FILTERS.map(s => (
            <div
              key={s}
              className={`sidebar-item ${selectedStatus === s ? 'active' : ''}`}
              onClick={() => setSelectedStatus(s)}
            >
              {s}
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{open}</span>
            <span className="stat-label">Open Tasks</span>
          </div>
          <div className="stat-item urgent">
            <span className="stat-value">{overdue}</span>
            <span className="stat-label">Overdue</span>
          </div>
          <div className="stat-item cost">
            <span className="stat-value">${totalCost.toLocaleString()}</span>
            <span className="stat-label">Est. Cost</span>
          </div>
          <button className="add-btn" onClick={openAdd}>+ Add Task</button>
        </div>

        {toast && (
          <div className="recurring-toast">{toast}</div>
        )}

        <div className="search-bar">
          <span className="search-icon">&#128269;</span>
          <input
            className="search-input"
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>&times;</button>
          )}
        </div>

        {/* Content */}
        <div className="content-area">
          <div className="content-header">
            <h2>{selectedProperty} — {selectedStatus} Tasks</h2>
            <p>{filtered.length} task{filtered.length !== 1 ? 's' : ''} {selectedPriority !== 'All' ? `· ${selectedPriority} priority` : ''}</p>
          </div>

          {loading ? (
            <div className="empty-state"><p>Loading tasks...</p></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              {searchTerm.trim() ? (
                <>
                  <h3>No results for "{searchTerm}"</h3>
                  <p>Try a different search term or clear filters</p>
                </>
              ) : (
                <>
                  <h3>No tasks here</h3>
                  <p>Add a task to get started</p>
                </>
              )}
            </div>
          ) : (
            <div className="task-list">
              {filtered.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  photos={taskPhotos.filter(p => p.task_id === task.id)}
                  onToggle={toggleDone}
                  onEdit={openEdit}
                  onDelete={deleteTask}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <TaskModal
          task={editingTask}
          properties={properties}
          existingPhotos={editingTask ? taskPhotos.filter(p => p.task_id === editingTask.id) : []}
          onSaved={onSaved}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
